import { accentRail } from "../config/designTokens";
import { baseUrl } from "../config/env";
import type { Faq } from "../seo/schema";

export const globalNav = [
  { label: "Review Management", href: "/google-review-management", start: false },
  { label: "Website Audit", href: "/website-audit-service", start: false },
  { label: "Start", href: "/#services", start: true }
] as const;

export function accentRailMarkup(className = "accent-rail"): string {
  return `<div class="${className}" aria-hidden="true">${accentRail
    .map((color) => `<span style="background:${color}"></span>`)
    .join("")}</div>`;
}

export function arrowList(items: readonly string[]): string {
  return items.map((item) => `<li><span>→</span>${item}</li>`).join("");
}

export function paragraphs(items: readonly string[]): string {
  return items.map((item) => `<p>${item}</p>`).join("");
}

const CHECK_SVG =
  '<svg viewBox="0 0 24 24" fill="none"><path d="m5 13 4 4 10-12" stroke="currentColor" stroke-width="2.5" stroke-linecap="square" stroke-linejoin="round" /></svg>';

export function checkList(items: readonly string[]): string {
  return items
    .map(
      (item) =>
        `<li><span class="check" aria-hidden="true">${CHECK_SVG}</span>${item}</li>`
    )
    .join("");
}

export function topNav(assetBase = baseUrl()): string {
  return `
    <nav class="top-nav" data-screen-label="Nav" aria-label="Primary">
      <a class="brand" href="/" aria-label="Foxmark home">
        <span class="brand-mark">
          <img src="${assetBase}assets/foxmark-logomark.png" alt="" width="40" height="47" />
        </span>
        <span class="brand-word">
          <span>Foxmark</span>
          ${accentRailMarkup("brand-rail")}
        </span>
      </a>
      <div class="nav-links">${globalNav
      .map(
        (link) =>
          `<a class="${link.start ? "nav-start" : ""}" href="${link.href}">${link.label}</a>`
      )
      .join("")}</div>
      <details class="mobile-nav">
        <summary aria-label="Open navigation">
          <span>Menu</span>
          <span class="mobile-nav-icon" aria-hidden="true"><span></span><span></span></span>
        </summary>
        <div class="mobile-nav-panel">${globalNav
      .map(
        (link) =>
          `<a class="${link.start ? "mobile-nav-start" : ""}" href="${link.href}">${link.label}</a>`
      )
      .join("")}</div>
      </details>
    </nav>
  `;
}

export function siteFooter(): string {
  return `
    <footer class="footer" data-screen-label="Footer">
      <div class="footer-inner">
        <div class="footer-brand">
          <span>Foxmark</span>
          <p>Monthly visibility, trust, and website reports for local businesses.</p>
        </div>
        <nav class="footer-nav" aria-label="Footer">
          <div class="footer-col">
            <p class="footer-heading">Services</p>
            <a href="/google-review-management">Google Review Management</a>
            <a href="/website-audit-service">Website Audit Service</a>
          </div>
          <div class="footer-col">
            <p class="footer-heading">For your business</p>
            <a href="/reputation-management-for-dentists">Dental practices</a>
            <a href="/marketing-for-med-spas">Med spas</a>
            <a href="/local-seo-for-contractors">Contractors</a>
            <a href="/seo-for-restaurants">Restaurants</a>
          </div>
          <div class="footer-col">
            <p class="footer-heading">Guides</p>
            <a href="/guides/respond-to-google-reviews">How to respond to Google reviews</a>
            <a href="/guides/get-more-google-reviews">How to get more Google reviews</a>
          </div>
        </nav>
      </div>
      <p class="footer-legal">© 2026 Foxmark. Monthly reputation and website reports for local businesses.</p>
    </footer>
  `;
}

export function faqSection(heading: string, faqs: readonly Faq[]): string {
  return `
    <section class="section section-soft faq-section" data-screen-label="FAQ">
      <div class="section-heading">
        ${accentRailMarkup()}
        <h2>${heading}</h2>
      </div>
      <div class="faq-list">${faqs
      .map(
        (faq) =>
          `<details class="faq-item"><summary>${faq.question}</summary><p>${faq.answer}</p></details>`
      )
      .join("")}</div>
    </section>
  `;
}

export function ctaBand(input: {
  headline: string;
  body: string;
  primaryHref: string;
  primaryLabel: string;
  secondaryHref: string;
  secondaryLabel: string;
  microcopy: string;
}): string {
  return `
    <section class="final-cta" data-screen-label="Final CTA">
      ${accentRailMarkup()}
      <h2>${input.headline}</h2>
      <p>${input.body}</p>
      <div class="actions actions-centered">
        <a class="button button-primary" href="${input.primaryHref}">${input.primaryLabel}</a>
        <a class="button button-outline" href="${input.secondaryHref}">${input.secondaryLabel}</a>
      </div>
      <p class="proof-line">${input.microcopy}</p>
    </section>
  `;
}
